import { Component, Input, ViewChild } from '@angular/core';
// Added by Amisha on 2026-08-12.
// Reason: provide reusable activity-wise sharing for each inline application activity.
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Activity, ActivityModel } from 'src/app/models/activities';
import {
  ShareAccessPermission,
  SharedAccessDetails,
  SharedAcessActivity
} from 'src/app/models/shared-access-details';
import { DataService } from 'src/app/services';
import {
  ButtonStyle,
  ModalConfirmComponent
} from '../../system/modal-confirm/modal-confirm.component';

enum SharingStatus {
  Loading,
  Loaded,
  Sharing,
  Shared
}

@Component({
  selector: 'wm-share-activity',
  templateUrl: './share-activity.component.html',
  styleUrls: ['./share-activity.component.scss'],
  standalone: false
})
export class ShareActivityComponent {
  @Input() applicationId: string;
  @ViewChild('shareActivityModal', { static: false })
  private shareActivityModal: ModalConfirmComponent;

  activity: Activity<ActivityModel> | null = null;
  details: SharedAccessDetails | null = null;
  filteredPriorActivities: SharedAcessActivity[] = [];
  filteredFutureActivities: SharedAcessActivity[] = [];
  activitySearchQuery = '';
  activitySearchFieldConfig = { label: 'Search activities...' };
  shareForm = this.createShareForm();
  sharingStatus = SharingStatus.Loading;
  SharingStatus = SharingStatus;
  ButtonStyle = ButtonStyle;
  showCommandButton = true;
  cancelText = 'Cancel';

  constructor(private dataService: DataService) {}

  open(activity: Activity<ActivityModel>): void {
    if (!activity || !this.applicationId) {
      return;
    }

    this.activity = activity;
    this.details = null;
    this.activitySearchQuery = '';
    this.shareActivityModal.open();
  }

  getShareApplicationDetails(): void {
    if (!this.activity) {
      return;
    }

    this.shareForm = this.createShareForm();
    this.cancelText = 'Cancel';
    this.showCommandButton = true;
    this.sharingStatus = SharingStatus.Loading;

    this.dataService
      .getWorkflowApplicationShareDetails(this.applicationId, this.activity)
      .subscribe(details => {
        details.futureActivities = (details.futureActivities || []).filter(a =>
          this.canShareView(a)
        );
        details.priorActivities = (details.priorActivities || []).filter(a =>
          this.canShareView(a)
        );

        this.details = details;
        this.filteredFutureActivities = details.futureActivities;
        this.filteredPriorActivities = details.priorActivities;
        this.shareForm.controls['loaded'].setValue(true);
        this.sharingStatus = SharingStatus.Loaded;
      });
  }

  handleActivitiesSearchChange(value: string): void {
    this.activitySearchQuery = value || '';
    const query = this.activitySearchQuery.trim().toLowerCase();

    if (!this.details || !query) {
      this.filteredFutureActivities = this.details?.futureActivities || [];
      this.filteredPriorActivities = this.details?.priorActivities || [];
      return;
    }

    this.filteredFutureActivities = this.details.futureActivities.filter(a =>
      a.activityName.toLowerCase().includes(query)
    );
    this.filteredPriorActivities = this.details.priorActivities.filter(a =>
      a.activityName.toLowerCase().includes(query)
    );
  }

  shareApplication(): void {
    if (!this.details) {
      return;
    }

    this.sharingStatus = SharingStatus.Sharing;
    this.dataService.shareWorkflowApplication(this.details).subscribe(() => {
      this.sharingStatus = SharingStatus.Shared;
      this.showCommandButton = false;
      this.cancelText = 'Close';
      this.shareActivityModal.processing = false;
    });
  }

  showActivities(share: SharedAccessDetails): boolean {
    share.showDetails = !share.showDetails;
    if (share.showDetails && !share.activities) {
      this.dataService.getShareActivities(share.id).subscribe(activities => {
        share.activities = activities || [];
      });
    }
    return false;
  }

  removeSharedAccess(id: string): void {
    this.dataService.revokeSharedAccess(id).subscribe(() => {
      if (this.details) {
        this.details.shares = (this.details.shares || []).filter(
          share => share.id !== id
        );
      }
    });
  }

  canShareView(activity: SharedAcessActivity): boolean {
    return activity.availablePermissions?.includes(
      ShareAccessPermission.ReadOnly
    );
  }

  canEdit(activity: SharedAcessActivity): boolean {
    return activity.availablePermissions?.includes(ShareAccessPermission.Edit);
  }

  private createShareForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      emailAddress: new UntypedFormControl('', [
        Validators.required,
        Validators.email
      ]),
      loaded: new UntypedFormControl('', Validators.required)
    });
  }
}
